/**
 * Outreach gallery, assembled automatically from the files on disk.
 *
 * HOW IT WORKS
 *  - `scripts/generate-gallery-manifest.js` scans public/gallery/ and writes
 *    gallery.manifest.json (one { file } per image). It runs before every
 *    `npm start` / `npm run build`.
 *  - The CAPTIONS map below layers a human-readable caption + place/date onto
 *    a file by filename. Anything without an entry still appears, with a
 *    caption guessed from its filename.
 *
 * TO ADD A PHOTO
 *  1. Drop a PNG / JPG / WEBP into public/gallery/ with a descriptive,
 *     kebab-case filename (it becomes the fallback caption on its own).
 *  2. Optionally add a CAPTIONS entry below for a polished caption / place.
 *  3. Restart the dev server (the manifest regenerates on start).
 */
import manifest from "./gallery.manifest.json";

// Keyed by filename in public/gallery/. Every field is optional. `order`
// pins a photo near the front of the grid (lower = earlier).
const CAPTIONS = {};

function captionFromFilename(file) {
  return file
    .replace(/\.[^.]+$/, "")
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const gallery = manifest
  .map(({ file }, i) => {
    const meta = CAPTIONS[file] || {};
    return {
      id: file.replace(/\.[^.]+$/, ""),
      file,
      caption: meta.caption || captionFromFilename(file),
      // True only when a real caption was set in CAPTIONS above, so the UI
      // can hide filename-derived fallback text (e.g. on hover) instead of
      // showing something like "Whatsapp Image 2026 09 14 At 4.30.47 Am".
      hasCaption: Boolean(meta.caption),
      place: meta.place || "",
      date: meta.date || "",
      order: meta.order ?? 100 + i,
    };
  })
  .sort((a, b) => a.order - b.order || a.caption.localeCompare(b.caption));

export default gallery;
